/**
 * Resolves how the WorkOS AuthKit session is persisted, from environment
 * variables, so it can be configured per deployment without a code change.
 *
 * authkit-js `devMode` decides where the refresh token lives:
 *   - devMode true  → refresh token in `window.localStorage`. Survives reload,
 *     but iOS Safari ITP deletes all script-writable storage after ~7 days
 *     idle, so the token is purged and the user is silently logged out.
 *   - devMode false → session rides an httpOnly cookie on the Authentication
 *     API domain (`apiHostname`). Only durable on Safari when that domain is
 *     FIRST-PARTY — a subdomain of the app's own registrable domain — because
 *     Safari blocks third-party cookies outright.
 *
 * See specs/technical/workos-session-config.spec.md for the full rationale.
 */
export interface WorkOSAuthConfig {
  /** authkit-js `devMode`: true → localStorage refresh token; false → cookie. */
  devMode: boolean;
  /** Custom Authentication API domain (e.g. "auth.otus.build"); undefined → SDK default. */
  apiHostname: string | undefined;
}

/** The narrow slice of env this resolver reads. Kept small so tests pass plain objects. */
export interface WorkOSAuthEnv {
  VITE_WORKOS_API_HOSTNAME?: string;
  VITE_WORKOS_DEV_MODE?: string;
}

export function resolveWorkOSAuthConfig(env: WorkOSAuthEnv): WorkOSAuthConfig {
  const apiHostname = env.VITE_WORKOS_API_HOSTNAME || undefined;
  const override = env.VITE_WORKOS_DEV_MODE;

  // An explicit VITE_WORKOS_DEV_MODE wins — used to force cookie mode on a
  // preview (`"false"`, no domain) to reproduce the third-party-cookie logout,
  // or to force localStorage on (`"true"`). Empty string counts as unset.
  // Otherwise derive from the domain: a configured first-party auth domain
  // means the cookie is safe, so devMode is off; with no domain we must stay on
  // localStorage — a localhost / preview origin can't be same-site with an auth
  // domain, so the cookie would be third-party and Safari would block it.
  const devMode =
    override != null && override !== ""
      ? override !== "false"
      : apiHostname === undefined;

  return { devMode, apiHostname };
}
